import React, { FC, useEffect, useState, useCallback, useMemo } from "react";
import { MenuItem } from "@mui/material";
import { useForm, useWatch } from 'react-hook-form';
import { FormMaterialSelector } from "components/Selector";
import FixedLoader from "components/FixedLoader/FixedLoader";
import MaxBookedPopup from "./MaxBookedPopup";
import { ContainedButton, OutlinedButton } from "../AvailableJob.styles";
import styles from "./components.module.scss";
import { HygienistProps, JobProps, PartialOffer } from "utils/types";
import { JOB_STATUSES, PARTIAL_OFFER_STATUS, MINIMUM_PARTIAL_SHIFT_HOURS } from "utils/constants";
import useActionApi from "../../../../hooks/useActionApi";
import Api from "../../../../api";
import _ from "lodash";
import { formatDateTo12HourTimeString, formatDateTo24HourTimeString, stringToDate } from "utils/time";
import moment from "moment";

interface MakePartialOfferProps {
    hygienist: HygienistProps,
    job: JobProps,
    onClose: () => void,
    onSubmit: (selectedStartTime: string, selectedEndTime: string, rate: number, isPartial?: boolean) => void
}

const generateTimeOptions = (from = 5, to = 23, step = 15) => {
    const times = [];
    for (let hour = from; hour <= to; hour++) {
        for (let minute = 0; minute < 60; minute += step) {
            const time = new Date(); time.setHours(hour, minute);
            times.push({
                value: formatDateTo24HourTimeString(time),
                label: formatDateTo12HourTimeString(time),
                timestamp: time.valueOf()
            });
        }
    }
    return times;
};

const CalculateShiftRateOptions = (hygienist: HygienistProps, job: JobProps): JSX.Element[] => {
    const last = (array: number[]) => array[array.length - 1];
    const getArrayInRange = (min: number, max: number) => _.range(min, max + 1);

    let options: JSX.Element[] = [
        <MenuItem key={'noValue'} disabled>Select Hourly Rate</MenuItem>
    ];

    let proposedRate = job.proposedRate ?? job.avgRate;

    const greenRate = getArrayInRange(proposedRate - 5, proposedRate + 2);
    let yellowRate = getArrayInRange(last(greenRate) + 1, last(greenRate) + 4);
    const redRate: number[] = [];

    const SATURDAY = 6; // week start at '0' for 'Sunday'
    const isSaturdayShift = stringToDate(job.startDate).getDay() === SATURDAY; 

    if (isSaturdayShift || hygienist.status === 1 || hygienist.status === 2) {
        yellowRate.push(...getArrayInRange(last(yellowRate) + 1, last(yellowRate) + 3));
    }

    if (hygienist.status === 2) {
        redRate.push(...getArrayInRange(last(yellowRate) + 1, last(yellowRate) + 3));
    }

    const allRates = [...greenRate, ...yellowRate, ...redRate];
    const allRateItems = allRates.map(rate => (
        <MenuItem key={rate} value={rate}>
            {rate === proposedRate ? (
                <div className={styles.rateSuggested}><b>${rate}/hr</b> Suggested Rate</div>
            ) : `$${rate}/hr`}
        </MenuItem>
    ));

    options.push(...allRateItems);

    if (!isSaturdayShift && hygienist.status === 0) {
        const disabledRates = getArrayInRange(last(allRates) + 4, last(allRates) + 6).map(rate => (
            <MenuItem key={rate} disabled value={rate}>
                {`$${rate}/hr (Earn "Elite" Status to Unlock)`}
            </MenuItem>
        ));

        const additionalDisabledRates = getArrayInRange(last(allRates) + 1, last(allRates) + 3).map(rate => (
            <MenuItem key={rate} disabled value={rate}>
                {`$${rate}/hr (Earn "Elite" Status to Unlock)`}
            </MenuItem>
        ));

        options.push(...disabledRates, ...additionalDisabledRates);
    }

    if (!isSaturdayShift && hygienist.status === 1) {
        const disabledRates = getArrayInRange(last(allRates) + 1, last(allRates) + 3).map(rate => (
            <MenuItem key={rate} disabled value={rate}>
                {`$${rate}/hr (Earn "Elite" Status to Unlock)`}
            </MenuItem>
        ));
        options.push(...disabledRates);
    }

    return options;
};

const convertToTimeString = (timeString: string): string => {
    // Simply return the time in HH:mm format for the backend to handle timezone conversion
    return timeString;
};

const MakePartialOfferPopup: FC<MakePartialOfferProps> = React.memo(({
    hygienist,
    job,
    onClose,
    onSubmit
}) => {
    const shift = job.shifts[0];
    const shiftStart: Date = stringToDate(shift.postedStart, shift.shiftDate);
    const shiftEnd: Date = stringToDate(shift.postedEnd, shift.shiftDate);

    const defaultRate = !!(job?.ghr) 
        ? job.ghr.toString() 
        : job.proposedRate?.toString() || job.avgRate?.toString();

    const { control, handleSubmit, setValue, watch } = useForm({
        defaultValues: {
            selectedStartTime: formatDateTo24HourTimeString(shiftStart),
            selectedEndTime: formatDateTo24HourTimeString(shiftEnd),
            rate: defaultRate,
        }
    });

    const selectedStartTime = useWatch({ control, name: "selectedStartTime" });
    const selectedEndTime = useWatch({ control, name: "selectedEndTime" });
    const rate = useWatch({ control, name: "rate" });

    const shiftRateOptions = useMemo(() => 
        CalculateShiftRateOptions(hygienist, job)
    , [hygienist?.status, job?.proposedRate, job?.avgRate]);

    const totalHours: string = useMemo<string>(() => {
        const fallback = '00:00';

        if (!selectedStartTime || !selectedEndTime) {
            return fallback;
        }

        const start = stringToDate(selectedStartTime, shift.shiftDate);
        const end = stringToDate(selectedEndTime, shift.shiftDate);

        const diffMs = end.getTime() - start.getTime();
        if (diffMs < 0) {
            return fallback;
        }

        const totalMinutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }, [selectedStartTime, selectedEndTime, shift.shiftDate]);

    const formatHoursForDisplay = useCallback((timeString: string): string => {
        if (!timeString || timeString === '00:00') {
            return '00:00';
        }
        
        const [hours, minutes] = timeString.split(':').map(Number);
        
        return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }, []);

    const [isMaxBookedPopupOpen, setIsMaxBookedPopupOpen] = useState<boolean>(false);
    const [isMaxBookedOffers, setIsMaxBookedOffers] = useState<boolean>(false);
    const [isMaxConfirmedJobs, setIsMaxConfirmedJobs] = useState<boolean>(false);

    const [getHygienistOffersLoading, , getHygienistOffers] = useActionApi({
        apiMethod: Api.getHygienistOffers,
        onSuccess: (offers: PartialOffer[]) => {
            const postedOffers = offers.filter(offer => offer.status === PARTIAL_OFFER_STATUS.POSTED)
            setIsMaxBookedOffers(postedOffers.length >= hygienist?.maxBooked * 2);
        }
    });

    const [getHygienistJobsLoading, , getHygienistJobs] = useActionApi({
        apiMethod: Api.getHygienistJobs,
        onSuccess: (jobs: JobProps[]) => {
            const confirmedJobs = jobs.filter(job => job.status === JOB_STATUSES.CONFIRMED)
            setIsMaxConfirmedJobs(confirmedJobs.length >= hygienist?.maxBooked);
        }
    });

    const timeOptions = useMemo(() => {
        const hoursToMs = (hours: number) => hours * 60 * 60 * 1000;
        const latestStart = new Date(shiftEnd.getTime() - hoursToMs(MINIMUM_PARTIAL_SHIFT_HOURS));
        
        const allOptions = generateTimeOptions(shiftStart.getHours(), shiftEnd.getHours());
        
        return allOptions.filter(option => {
            const optionTime: Date = stringToDate(option.value, shift.shiftDate);
            return optionTime.getTime() >= shiftStart.getTime() && optionTime.getTime() <= latestStart.getTime();
        });
    }, [shiftStart, shiftEnd, shift.shiftDate, MINIMUM_PARTIAL_SHIFT_HOURS]);

    const endTimeOptions = useMemo(() => {
        if (!selectedStartTime) return [];
        const startDateTime: Date = stringToDate(selectedStartTime, shift.shiftDate);

        const hoursToMs = (hours: number) => hours * 60 * 60 * 1000;
        const earliestEnd = new Date(startDateTime.getTime() + hoursToMs(MINIMUM_PARTIAL_SHIFT_HOURS));
        
        const allOptions = generateTimeOptions(earliestEnd.getHours(), shiftEnd.getHours(), 15);
        return allOptions.filter(option => {
            const optionTime: Date = stringToDate(option.value, shift.shiftDate);
            return optionTime.getTime() >= earliestEnd.getTime() && optionTime.getTime() <= shiftEnd.getTime();
        });
    }, [selectedStartTime, shift.shiftDate, shiftEnd]);

    useEffect(() => {
        getHygienistJobs('');
        getHygienistOffers({});
    }, []);

    useEffect(() => {
        if (selectedStartTime && selectedEndTime) {
            const start = stringToDate(selectedStartTime, shift.shiftDate);
            const end = stringToDate(selectedEndTime, shift.shiftDate);

            const diffMs = end.getTime() - start.getTime();
            const diffInHours = diffMs / 1000 / 60 / 60;

            if (diffInHours < MINIMUM_PARTIAL_SHIFT_HOURS) {
                setValue('selectedEndTime', '');
            }
        }
    }, [selectedStartTime, selectedEndTime, setValue, shift]);

    const renderStartTimeOption = useCallback((timeOption: { value: string; label: string; timestamp: number }) => (
        <MenuItem
            key={timeOption.value}
            value={timeOption.value}
        >
            {timeOption.label}
        </MenuItem>
    ), []);

    const renderEndTimeOption = useCallback((timeOption: { value: string; label: string; timestamp: number }) => (
        <MenuItem
            key={timeOption.value}
            value={timeOption.value}
        >
            {timeOption.label}
        </MenuItem>
    ), []);

    const handleFormSubmit = useCallback((data: any) => {
        if (isMaxBookedOffers || isMaxConfirmedJobs) {
            setIsMaxBookedPopupOpen(true);
            return;
        }
        // Send simple time strings - backend will handle timezone conversion using job location
        const startTime = convertToTimeString(data.selectedStartTime);
        const endTime = convertToTimeString(data.selectedEndTime);
        onSubmit(startTime, endTime, Number(data.rate), true);
    }, [isMaxBookedOffers, isMaxConfirmedJobs, onSubmit]);

    const isSubmitButtonDisabled = useMemo(() => {
        if (!selectedEndTime || !selectedStartTime) {
            return true;
        }

        const start: Date = stringToDate(selectedStartTime, shift.shiftDate);
        const end: Date = stringToDate(selectedEndTime, shift.shiftDate);
        if (start.getTime() >= end.getTime()) {
            return true;
        }

        const hoursToMs = (h: number) => h * 60 * 60 * 1000;
        const diffMs = end.getTime() - start.getTime();
        if (diffMs < hoursToMs(MINIMUM_PARTIAL_SHIFT_HOURS)) {
            return true;
        }

        return false;
    }, [selectedEndTime, selectedStartTime, shift.shiftDate, MINIMUM_PARTIAL_SHIFT_HOURS]);

    return (
        <div className={styles.partialOfferContainer}>
            <div className={styles.titleContainer}>
                <h2 className={styles.partialOfferTitle}>Partial Shift Offer</h2>
                <p className={styles.popupSubtitle}>READ CAREFULLY</p>
            </div>

            <p className={styles.partialOfferDescription}>
                Submit an offer to work part of this shift by selecting your hours and hourly rate.
            </p>

            <div className={styles.availableHours}>
                Available Shift Hours: {job.shifts[0].postedStart} - {job.shifts[0].postedEnd}
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <div className={styles.timeInputs}>
                    <div className={styles.timePickerBox}>
                        <FormMaterialSelector
                            control={control}
                            name="selectedStartTime"
                            label="Your Start Time"
                        >
                            {[
                                <MenuItem key="selectStartTime" value="">Select Start Time</MenuItem>,
                                ...timeOptions.map(renderStartTimeOption)
                            ]}
                        </FormMaterialSelector>
                        <div className={styles.helperText}>
                            Select the time you will start your shift
                        </div>
                    </div>

                    <div className={styles.timePickerBox}>
                        <FormMaterialSelector
                            control={control}
                            name="selectedEndTime"
                            label="Your End Time"
                        >
                            {[
                                <MenuItem key="selectEndTime" value="">Select End Time</MenuItem>,
                                ...endTimeOptions.map(renderEndTimeOption)
                            ]}
                        </FormMaterialSelector>
                        <div className={styles.helperText}>
                            Select the time you will end your shift (minimum {MINIMUM_PARTIAL_SHIFT_HOURS} hour duration)
                        </div>
                    </div>
                </div>

                <div className={styles.hoursDisplay}>
                    Total Hours: {formatHoursForDisplay(totalHours)} hrs
                </div>

                <div className={styles.inputContainer}>
                    <FormMaterialSelector
                        control={control}
                        name="rate"
                        label="Your Hourly Rate"
                        className={styles.rateInputContainer}
                    >
                        {!!job?.ghr ? [
                            <MenuItem key={'isGuaranteedRate'} value={job.ghr}>
                                ${job.ghr}/hr Guaranteed
                            </MenuItem>
                        ] : shiftRateOptions}
                    </FormMaterialSelector>
                </div>

                <div className={styles.partialOfferButtons}>
                    <ContainedButton
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isSubmitButtonDisabled}
                    >
                        Submit Partial Shift Offer
                    </ContainedButton>

                    <OutlinedButton
                        variant="outlined"
                        size="large"
                        onClick={onClose}
                    >
                        Cancel
                    </OutlinedButton>
                </div>
            </form>

            <MaxBookedPopup
                isOpen={isMaxBookedPopupOpen}
                onClose={() => setIsMaxBookedPopupOpen(false)}
                hygienistStatus={hygienist?.status}
                completedShifts={hygienist?.completedJobs}
            />

            <FixedLoader
                loading={getHygienistOffersLoading || getHygienistJobsLoading}
            />
        </div>
    );
});

export default MakePartialOfferPopup;
